/**
 * CLIENTS → ÉCHÉANCES & RELANCES — qui doit quoi au magasin, et depuis quand.
 *
 * POURQUOI CET ÉCRAN EXISTE. La caisse tient cet échéancier depuis toujours (§13.6 D) et le pousse
 * par son canal ; RIEN au back-office ne le lisait. Le siège ne savait donc pas qui devait de
 * l'argent. ⚠️ **Le manque n'était pas une surface d'ÉCRITURE** — une échéance se crée au comptoir,
 * avec le client en face, et c'est bien là qu'elle doit se créer — mais une surface de LECTURE
 * (décision utilisateur). Cet écran est donc DÉLIBÉRÉMENT en lecture seule.
 *
 * ⚠️ « EN RETARD » SE DÉRIVE, IL NE SE STOCKE PAS, et le schéma l'écrit lui-même. Une échéance
 * devient en retard sans que personne n'écrive rien : un drapeau posé hier serait faux aujourd'hui.
 * Le serveur le calcule à l'instant de la lecture.
 *
 * ⚠️ LE MOYEN ANNONCÉ N'EST PAS UN RÈGLEMENT. Le client a DIT qu'il paierait par chèque ; rien n'a
 * été encaissé. L'écran le nomme « annoncé » — le rendre comme un moyen de paiement ferait lire
 * « payé par chèque » là où il faut lire « a annoncé un chèque » (§23.0).
 *
 * ⚠️ ET LE NUMÉRO DE PIÈCE EST RECOPIÉ, JAMAIS UNE CLÉ : il informe, il n'ouvre rien. En faire un
 * lien promettrait une navigation qui n'existe pas.
 *
 * ⚠️ Le sélecteur « Normal / Vide / Échec » du dessin est une commande de DÉMONSTRATION : il n'a pas d'équivalent ici,
 * chaque état vient de la lecture réelle.
 */
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { DANGER_SOFT_BORDER, EmptyState, Icon, Trans, type IconName } from '@orgazone/ui-web';
import { reminderMethodText } from '@orgazone/sync-contract';
import { api, type EcheancierDuSiege, type RelanceDuSiege } from '../api';
import { dateCourte, euros } from './titres';
import { t } from '../i18n';

/** Le mot d'un moyen annoncé, traduit ; le code brut s'il est inconnu. */
function methodWord(code: string): string {
  const word = reminderMethodText(code);
  return word ? t(word) : code;
}

/** Les colonnes du tableau, partagées par l'en-tête et chaque ligne — sinon une colonne glisserait sous un autre titre. */
const COLUMNS = '104px minmax(150px,1fr) 104px minmax(0,150px) minmax(0,130px) 128px';

/**
 * Combien de clients relancer : les échéances en retard, une fois par client.
 * ⚠️ PAR CLIENT, PAS PAR ÉCHÉANCE : trois échéances d'un même client en retard font UN appel à passer. Une échéance
 *    sans client (un acompte de passage) compte pour elle-même — quelqu'un doit cet argent.
 */
export function clientsToRemind(rows: readonly RelanceDuSiege[]): number {
  return new Set(rows.filter((r) => r.overdue).map((r) => r.clientId ?? `reminder:${r.id}`)).size;
}

export function Relances() {
  const [jeu, setJeu] = useState<EcheancierDuSiege | null>(null);
  const [echec, setEchec] = useState<string | null>(null);

  // ⚠️ « Réessayer » repasse l'écran en lecture et disparaît avec l'échec : deux lectures ne peuvent pas se croiser.
  const relire = useCallback(() => {
    setEchec(null);
    setJeu(null);
    api.relances()
      // ⚠️ L'ÉCHEC SE DIT : un échéancier vide se lirait « personne ne doit rien », et le siège
      //    cesserait de relancer — exactement le contraire de ce que cet écran sert à faire.
      .then(setJeu)
      .catch((e) => { setEchec(e instanceof Error ? e.message : String(e)); });
  }, []);
  useEffect(relire, [relire]);

  const due = jeu?.rows.filter((r) => r.status !== 'done') ?? [];
  const settled = jeu?.rows.filter((r) => r.status === 'done') ?? [];
  const toRemind = clientsToRemind(due);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
          {t('Échéances & relances')}
        </h1>
        <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)', textWrap: 'pretty' }}>
          {t('Ce que les clients ont annoncé devoir régler, tenu en caisse. Une échéance se crée au comptoir — ici, on la consulte.')}
        </p>
      </div>

      {echec ? (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 'var(--r-md)', background: 'var(--rose-bg)', border: `1px solid ${DANGER_SOFT_BORDER}` }}>
          <Icon name="alert" size={18} stroke={2} style={{ color: 'var(--rose-fg)', flexShrink: 0 }} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', lineHeight: 1.5 }}>
            <Trans k='<1>L’échéancier n’a pas pu être lu</1> : {failure}. Ce n’est pas « personne ne doit rien ».'
              vars={{ failure: echec }} parts={[(x) => <strong>{x}</strong>]} />
          </span>
          <button type="button" onClick={relire}
            style={{ height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
            {t('Réessayer')}
          </button>
        </div>
      ) : jeu === null ? (
        <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture de l’échéancier…')}</p>
      ) : jeu.rows.length === 0 ? (
        <EmptyState icon="calendar" title={t('Aucune échéance')}
          note={t('Personne n’a d’échéance en cours dans ce magasin. Elles se créent en caisse, quand un client annonce un règlement à venir.')} />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 14 }}>
            <Tile icon="card" label={t('Reste à encaisser')} value={euros(jeu.dueCents)}
              detail={t('{n} échéance à encaisser', { n: due.length })} />
            {/* ⚠️ LE RETARD SE MONTRE À PART, et pas comme une part du total : c'est LUI qui décide
                s'il faut appeler quelqu'un aujourd'hui. Noyé dans le reste, il ne se voit plus. */}
            <Tile icon="clock" label={t('Dont en retard')} value={euros(jeu.overdueCents)} tone={jeu.overdueCents > 0 ? 'late' : 'quiet'}
              detail={toRemind > 0 ? t('{n} client à relancer aujourd’hui', { n: toRemind }) : t('Aucun retard')} />
          </div>

          <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 760 }}>
                <div style={{ ...ROW, padding: '9px 16px', background: 'var(--ink-50)', ...MONO }}>
                  <span>{t('Échéance')}</span><span>{t('Client')}</span><span style={{ textAlign: 'right' }}>{t('Montant')}</span>
                  <span>{t('Moyen annoncé')}</span><span>{t('Pièce d’origine')}</span><span>{t('État')}</span>
                </div>
                {due.map((r) => <Row key={r.id} r={r} />)}
                {settled.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-100)' }}>
                      <span style={MONO}>{t('Déjà réglées')}</span>
                      {/* ⚠️ LE SERVEUR NE REND QUE LES PLUS RÉCENTES : quand il en reste d'autres, l'écran le dit —
                          sinon le compte des lignes passerait pour celui de l'échéancier. */}
                      <span style={{ flex: 1, fontSize: 'var(--t-micro)', color: 'var(--ink-500)' }}>
                        {jeu.settledTotal > settled.length
                          ? t('{shown} sur {n} — les plus récentes', { shown: settled.length, n: jeu.settledTotal })
                          : t('{n} échéance réglée', { n: settled.length })}
                      </span>
                    </div>
                    {settled.map((r) => <Row key={r.id} r={r} />)}
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <p style={{ margin: '10px 2px 0', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5, textWrap: 'pretty' }}>
            <Trans k='Lecture seule. Le moyen affiché est celui que le client a <1>annoncé</1> — rien n’est encaissé tant que la caisse ne l’a pas réglé. « En retard » se calcule à l’ouverture de l’écran ; une échéance réglée n’est jamais en retard. Hors fiscal : aucune incidence sur le Z, le CA ou la TVA.'
              parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </p>
        </>
      )}
    </div>
  );
}

const ROW: CSSProperties = { display: 'grid', gridTemplateColumns: COLUMNS, gap: 12, padding: '11px 16px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' };
const MONO: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: '10px', fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--ink-500)' };
const CLIP: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const PILL: CSSProperties = { display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', fontSize: 'var(--t-micro)', fontWeight: 700, whiteSpace: 'nowrap' };

/** Les trois tons d'une tuile : un montant ordinaire, un retard à traiter, un zéro qui n'appelle rien. */
const TILE_TONES = {
  plain: { value: 'var(--ink-900)', icon: 'var(--ink-700)', iconBg: 'var(--ink-100)', border: 'var(--ink-200)' },
  late: { value: 'var(--rose-fg)', icon: 'var(--rose-fg)', iconBg: 'var(--rose-bg)', border: DANGER_SOFT_BORDER },
  quiet: { value: 'var(--ink-400)', icon: 'var(--ink-400)', iconBg: 'var(--ink-100)', border: 'var(--ink-200)' },
} as const;

/** Une tuile de l'en-tête : son montant, et ce qu'il recouvre. */
function Tile({ icon, label, value, detail, tone = 'plain' }: {
  icon: IconName; label: string; value: string; detail: string; tone?: keyof typeof TILE_TONES;
}) {
  const c = TILE_TONES[tone];
  return (
    <div style={{ padding: '13px 16px', background: 'var(--paper)', border: `1px solid ${c.border}`, borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', background: c.iconBg, color: c.icon, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={icon} size={17} stroke={1.9} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{label}</span>
        <span style={{ display: 'block', marginTop: 2, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em', color: c.value, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        <span style={{ display: 'block', fontSize: 'var(--t-micro)', color: 'var(--ink-500)' }}>{detail}</span>
      </span>
    </div>
  );
}

/** Une échéance : due (à venir ou en retard), ou réglée — grisée, avec sa date d'encaissement. */
function Row({ r }: { r: RelanceDuSiege }) {
  const settled = r.status === 'done';
  const faded = settled ? 'var(--ink-400)' : 'var(--ink-500)';
  return (
    <div style={ROW}>
      <span style={{ fontSize: 'var(--t-dense)', fontWeight: r.overdue ? 700 : 600, color: settled ? 'var(--ink-500)' : r.overdue ? 'var(--rose-fg)' : 'var(--ink-700)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {dateCourte(r.dueDate)}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: settled || !r.clientName ? 'var(--ink-500)' : 'var(--ink-900)', fontStyle: r.clientName ? 'normal' : 'italic', ...CLIP }}>
          {/* ⚠️ « client non précisé » et non un tiret : une échéance sans client existe (un acompte
              de passage), et un tiret se lirait comme une donnée manquante. */}
          {r.clientName ?? t('client non précisé')}
        </span>
        {r.note ? (
          <span style={{ display: 'block', fontSize: 'var(--t-micro)', color: faded, marginTop: 1, ...CLIP }}>{r.note}</span>
        ) : null}
      </span>
      <span style={{ textAlign: 'right', fontSize: 'var(--t-lead)', fontWeight: 700, color: settled ? 'var(--ink-400)' : 'var(--ink-900)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {euros(r.amountCents)}
      </span>
      <span style={{ fontSize: '12.5px', color: faded, minWidth: 0, ...CLIP }}>
        {/* ⚠️ « annoncé » est le mot juste : rien n'a été encaissé. Le code est NOMMÉ par la table du contrat
            (la même que la caisse) ; un code inconnu reste affiché tel quel plutôt que deviné. */}
        {r.expectedMethod ? t('{v} annoncé', { v: methodWord(r.expectedMethod) }) : ''}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', color: faded, minWidth: 0, ...CLIP }}>
        {r.sourcePieceNumber ?? ''}
      </span>
      <span>
        {settled ? (
          <span style={{ ...PILL, background: 'var(--ink-100)', color: 'var(--ink-600)' }}>{t('réglé le {v}', { v: dateCourte(r.doneAt) })}</span>
        ) : r.overdue ? (
          <span style={{ ...PILL, background: 'var(--rose-bg)', color: 'var(--rose-fg)' }}>{t('en retard')}</span>
        ) : (
          <span style={{ ...PILL, background: 'var(--teal-100)', color: 'var(--teal-800)' }}>{t('à venir')}</span>
        )}
      </span>
    </div>
  );
}
